import { Backdrop } from "@mui/material";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
  AddressElement,
} from "@stripe/react-stripe-js";
import {
  loadStripe,
  SetupIntent,
  StripeElementsOptions,
  StripeError,
} from "@stripe/stripe-js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import DotElastic from "components/DotElastic";
import { STRIPE_KEY } from "constants/env";
import { SetupIntentCompact } from "models/Payment";
import { Subscription } from "models/User";
import { createSetupIntent } from "services/SubscriptionService";
import { getUser } from "utils/helper";

import styles from "./AddPaymentMethod.module.scss";

export interface PaymentMethodFormRef {
  confirm: () => void;
}

interface PaymentMethodFormProps {
  onSuccess?: (setupIntent: SetupIntent) => void;
  onError?: (error?: StripeError) => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  returnUrl: string;
}

const PaymentMethodForm = ({
  onSuccess,
  onError,
  setIsLoading,
  returnUrl,
  ref,
}: PaymentMethodFormProps & { ref?: React.Ref<PaymentMethodFormRef> }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleConfirmation = async () => {
    if (!stripe || !elements) return;
    setIsLoading(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${returnUrl}`,
      },
      redirect: "if_required",
    });

    setIsLoading(false);

    if (error) {
      console.error("Error confirming setup:", error.message);
      onError?.(error);
    } else {
      console.log("Payment method added successfully!");
      onSuccess?.(setupIntent);
    }
  };

  React.useImperativeHandle(ref, () => ({
    confirm() {
      handleConfirmation();
    },
  }));

  return (
    <div>
      <PaymentElement />
      <AddressElement
        options={{
          mode: "billing",
          fields: { phone: "never" },
          defaultValues: {
            name: `${getUser()?.name} ${getUser()?.last_name || ""}` || "",
            address: {
              country: window.navigator.language.split("-")[1],
              state: "PLACEHOLDER", // Placeholder value to display expanded address form by default
            },
          },
        }}
      />
    </div>
  );
};

export interface AddPaymentMethodRef {
  confirm: () => void;
}

interface AddPaymentMethodProps {
  onSuccess?: (setupIntent: SetupIntent) => void;
  onError?: (error?: StripeError) => void;
  returnUrl: string;
  subscription: Subscription;
}

const AddPaymentMethod = ({
  onSuccess,
  onError,
  returnUrl,
  subscription,
  ref,
}: AddPaymentMethodProps & { ref?: React.Ref<AddPaymentMethodRef> }) => {
  const stripePromise = useMemo(() => loadStripe(STRIPE_KEY), []);

  const [setupIntent, setSetupIntent] = useState<SetupIntentCompact | null>(
    null,
  );
  const paymentMethodFormRef = useRef<PaymentMethodFormRef>(null);

  const [isLoading, setIsLoading] = useState(false);

  React.useImperativeHandle(ref, () => ({
    confirm() {
      paymentMethodFormRef.current?.confirm();
    },
  }));

  useEffect(() => {
    const getSetupIntent = async () => {
      const data: SetupIntentCompact = await createSetupIntent(subscription.id);
      setSetupIntent(data);
    };

    getSetupIntent();
  }, [subscription.id]);

  const options: StripeElementsOptions = {
    clientSecret: setupIntent?.client_secret,
    fonts: [
      {
        cssSrc:
          "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Reddit+Sans&display=swap",
      },
    ],
    appearance: {
      theme: "night",
      variables: {
        fontFamily: "Inter",
        colorBackground: "#16181E",
        borderRadius: "6px",
        colorDanger: "#FF4545",
        colorText: "#F7F7F7CC",
        colorPrimary: "#6565EC",
      },
    },
  };

  return (
    <div className={styles.container}>
      {setupIntent ? (
        <Elements stripe={stripePromise} options={options}>
          <PaymentMethodForm
            ref={paymentMethodFormRef}
            onSuccess={onSuccess}
            onError={onError}
            setIsLoading={setIsLoading}
            returnUrl={returnUrl}
          />
        </Elements>
      ) : (
        <div className={styles.loading}>
          <DotElastic />
        </div>
      )}

      <Backdrop open={isLoading} style={{ zIndex: 1 }}>
        <img src="/loading.gif" alt="" />
      </Backdrop>
    </div>
  );
};

export default AddPaymentMethod;
